import api from './api';
import { API_ENDPOINTS } from '../utils/constants';
import { dedupeInflight } from '../utils/dedupedFetch';

/**
 * Events API Service
 * Handles all events-related API calls
 */
export const eventsApi = {
  /**
   * Get all events with optional pagination
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of events to return
   * @param {number} params.offset - Offset for pagination
   * @returns {Promise<Object>} Events data with pagination
   */
  getAllEvents: (params = {}) => {
    const limit = params.limit ?? '';
    const offset = params.offset ?? '';
    return dedupeInflight(
      `events:list:${limit}:${offset}`,
      async () => {
        const response = await api.get(API_ENDPOINTS.EVENTS, { params });
        return response.data;
      },
      { cacheMs: 120000 }
    );
  },

  /**
   * Get events in spotlight (inSpotlight: true)
   * @returns {Promise<Array>} Array of spotlight events
   */
  getSpotlightEvents: () =>
    dedupeInflight(
      'events:spotlight',
      async () => {
        const data = await eventsApi.getAllEvents();
        const events = data.events || [];

        let spotlightEvents = events.filter((event) => event.inSpotlight === true);

        const todayDateOnly = new Date();
        todayDateOnly.setHours(0, 0, 0, 0);

        spotlightEvents = spotlightEvents.filter((event) => {
          try {
            if (!event.endDate) return true;

            const eventEndDate = new Date(event.endDate);
            eventEndDate.setHours(0, 0, 0, 0);

            if (isNaN(eventEndDate.getTime())) return true;

            return eventEndDate >= todayDateOnly;
          } catch {
            return true;
          }
        });

        return spotlightEvents;
      },
      { cacheMs: 120000 }
    ),

  /**
   * Search events by city/state with filters
   * @param {Object} params - Search parameters
   * @param {string} params.city - City name
   * @param {string} params.state - State name
   * @param {string} params.date - Event date (YYYY-MM-DD)
   * @param {string} params.category - Event category
   * @param {number} params.minPrice - Minimum price filter
   * @param {number} params.maxPrice - Maximum price filter
   * @returns {Promise<Array>} Filtered events
   */
  searchEvents: async (params = {}) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENTS_SEARCH, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get event by ID
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Event data
   */
  getEventById: async (eventId) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENTS_BY_ID(eventId));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get events by city
   * @param {string} city - City name
   * @returns {Promise<Array>} Events in the specified city
   */
  getEventsByCity: async (city) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENTS_SEARCH, {
        params: { city, state: '' }
      });
      return response.data.events || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get events by category
   * @param {string} category - Event category
   * @returns {Promise<Array>} Events in the specified category
   */
  getEventsByCategory: async (category) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENTS_SEARCH, {
        params: { category }
      });
      return response.data.events || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get upcoming events
   * @param {number} days - Number of days ahead to look
   * @returns {Promise<Array>} Upcoming events
   */
  getUpcomingEvents: (days = 7) => {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + days);
    const startDate = today.toISOString().split('T')[0];
    const endDate = futureDate.toISOString().split('T')[0];

    return dedupeInflight(
      `events:search:${startDate}:${endDate}`,
      async () => {
        const response = await api.get(API_ENDPOINTS.EVENTS_SEARCH, {
          params: { startDate, endDate }
        });

        const events = response.data.events || [];
        const todayDateOnly = new Date();
        todayDateOnly.setHours(0, 0, 0, 0);

        return events.filter((event) => {
          try {
            if (!event.endDate) return true;

            const eventEndDate = new Date(event.endDate);
            eventEndDate.setHours(0, 0, 0, 0);

            if (isNaN(eventEndDate.getTime())) return true;

            return eventEndDate >= todayDateOnly;
          } catch {
            return true;
          }
        });
      },
      { cacheMs: 120000 }
    );
  },

  /**
   * Get event shows/sessions for a specific event
   * @param {string} eventId - Event ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Array>} Available shows for the event on that date
   */
  getEventShows: async (eventId, date) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENT_SHOWS(eventId), {
        params: { date }
      });
      return response.data.shows || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get available dates for an event
   * @param {string} eventId - Event ID
   * @returns {Promise<Array>} Available dates for the event
   */
  getEventDates: async (eventId) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENT_DATES(eventId));
      return response.data.dates || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get ticket types for an event show
   * @param {string} eventId - Event ID
   * @param {string} showId - Show ID
   * @returns {Promise<Array>} Available ticket types
   */
  getTicketTypes: async (eventId, showId) => {
    try {
      const response = await api.get(API_ENDPOINTS.EVENT_TICKETS(eventId, showId));
      return response.data.ticketTypes || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a new event (Admin only)
   * @param {Object} eventData - Event data
   * @returns {Promise<Object>} Created event data
   */
  createEvent: async (eventData) => {
    try {
      const response = await api.post(API_ENDPOINTS.EVENTS, eventData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing event (Admin only)
   * @param {string} eventId - Event ID
   * @param {Object} eventData - Updated event data
   * @returns {Promise<Object>} Updated event data
   */
  updateEvent: async (eventId, eventData) => {
    try {
      const response = await api.put(API_ENDPOINTS.EVENTS_BY_ID(eventId), eventData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete an event (Admin only)
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteEvent: async (eventId) => {
    try {
      const response = await api.delete(API_ENDPOINTS.EVENTS_BY_ID(eventId));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===============================================
  // SHOWS MANAGEMENT FUNCTIONS
  // ===============================================

  /**
   * Create a new show for an event (Admin only)
   * @param {string} eventId - Event ID
   * @param {Object} showData - Show data (date, time, totalSeats, venue)
   * @returns {Promise<Object>} Created show data
   */
  createShow: async (eventId, showData) => {
    try {
      const response = await api.post(`/api/events/${eventId}/shows`, showData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing show (Admin only)
   * @param {string} eventId - Event ID
   * @param {string} showId - Show ID
   * @param {Object} showData - Updated show data
   * @returns {Promise<Object>} Updated show data
   */
  updateShow: async (eventId, showId, showData) => {
    try {
      const response = await api.put(`/api/events/${eventId}/shows/${showId}`, showData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a show (Admin only)
   * @param {string} eventId - Event ID
   * @param {string} showId - Show ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteShow: async (eventId, showId) => {
    try {
      const response = await api.delete(`/api/events/${eventId}/shows/${showId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===============================================
  // TICKET TYPES MANAGEMENT FUNCTIONS
  // ===============================================

  /**
   * Create a new ticket type for a show (Admin only)
   * @param {string} eventId - Event ID
   * @param {string} showId - Show ID
   * @param {Object} ticketData - Ticket data (name, description, price, taxes, totalQuantity)
   * @returns {Promise<Object>} Created ticket type data
   */
  createTicketType: async (eventId, showId, ticketData) => {
    try {
      const response = await api.post(`/api/events/${eventId}/shows/${showId}/tickets`, ticketData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing ticket type (Admin only)
   * @param {string} eventId - Event ID
   * @param {string} showId - Show ID
   * @param {string} ticketTypeId - Ticket Type ID
   * @param {Object} ticketData - Updated ticket data
   * @returns {Promise<Object>} Updated ticket type data
   */
  updateTicketType: async (eventId, showId, ticketTypeId, ticketData) => {
    try {
      const response = await api.put(`/api/events/${eventId}/shows/${showId}/tickets/${ticketTypeId}`, ticketData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a ticket type (Admin only)
   * @param {string} eventId - Event ID
   * @param {string} showId - Show ID
   * @param {string} ticketTypeId - Ticket Type ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteTicketType: async (eventId, showId, ticketTypeId) => {
    try {
      const response = await api.delete(`/api/events/${eventId}/shows/${showId}/tickets/${ticketTypeId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default eventsApi;
